import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Barcode,
  Contact,
  Grid2x2,
  Layers,
  Link2,
  Package,
  QrCode,
  Rows3,
  Target,
  Wifi,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { FaqSection } from "@/components/seo/FaqSection";
import { TableScroll } from "@/components/layout/ContentBlock";
import { absoluteUrl, breadcrumbJsonLd, ldScript, pageHead } from "@/lib/seo";

const PATH = "/generators";
const TITLE = "Barcode & QR Code Generators — Free Online Tools";
const DESC =
  "Free barcode and QR generators: UPC, EAN, Code 128, QR, Wi-Fi QR, vCard, Data Matrix and GS1 Digital Link. Browser-based, PNG or SVG output, no signup.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: PATH },
];

type GeneratorCard = {
  path: string;
  label: string;
  tagline: string;
  description: string;
  icon: typeof Barcode;
  formats: string[];
};

const GENERATORS: GeneratorCard[] = [
  {
    path: "/barcode-generator",
    label: "Barcode Generator",
    tagline: "Retail & logistics 1D codes",
    description:
      "Generate UPC-A, EAN-13/8, Code 128, Code 39, ITF-14 and Codabar barcodes with check-digit validation. Export print-ready PNG or scalable SVG.",
    icon: Barcode,
    formats: ["UPC-A", "EAN-13", "Code 128", "ITF-14"],
  },
  {
    path: "/bulk-barcode-generator",
    label: "Bulk Barcode Generator",
    tagline: "Many labels in one pass",
    description:
      "Paste a list of values and render every barcode at once. Per-row PNG / SVG, a combined SVG sheet or print straight from the browser.",
    icon: Layers,
    formats: ["Up to 200 lines", "Captions", "Print sheet", "SVG sheet"],
  },
  {
    path: "/qr-code-generator",
    label: "QR Code Generator",
    tagline: "Standard QR codes",
    description:
      "Build QR codes for any text, URL or payload. Pick error-correction level, size and foreground / background colours. PNG and SVG download.",
    icon: QrCode,
    formats: ["L / M / Q / H ECC", "Custom colour", "PNG", "SVG"],
  },
  {
    path: "/wifi-qr-code-generator",
    label: "Wi-Fi QR Code Generator",
    tagline: "One-scan Wi-Fi join",
    description:
      "Turn your network name, password and security type into a QR. Guests join without typing — perfect for cafes, offices and guest rooms.",
    icon: Wifi,
    formats: ["WPA / WPA2", "WEP", "Hidden SSID"],
  },
  {
    path: "/vcard-qr-generator",
    label: "vCard QR Code Generator",
    tagline: "Share contact details",
    description:
      "Encode a vCard (name, phone, email, company, URL) as a QR. Scanning saves the contact straight to a phone's address book.",
    icon: Contact,
    formats: ["vCard 3.0", "Phone / email / URL"],
  },
  {
    path: "/data-matrix-generator",
    label: "Data Matrix Generator",
    tagline: "Compact 2D for industry",
    description:
      "Encode text, UDI or GS1 payloads as a Data Matrix — the dense 2D symbol used on small parts, pharma and aerospace components.",
    icon: Grid2x2,
    formats: ["GS1 Data Matrix", "UDI", "Plain text"],
  },
  {
    path: "/gs1-digital-link-generator",
    label: "GS1 Digital Link Generator",
    tagline: "Sunrise 2027 ready",
    description:
      "Compose a GS1 Digital Link URL from a GTIN plus Application Identifiers (batch, expiry, serial) and render it as a QR or Data Matrix.",
    icon: Link2,
    formats: ["QR Code", "Data Matrix", "AIs (10, 17, 21…)"],
  },
  {
    path: "/itf-14-label-generator",
    label: "ITF-14 Carton Label Generator",
    tagline: "Shipping cartons & cases",
    description:
      "Print-ready ITF-14 carton barcodes with bearer bars and an optional GS1-128 batch/expiry secondary. SVG label sheet or PNG download.",
    icon: Package,
    formats: ["ITF-14", "Bearer bars", "GS1-128 (10) (17)"],
  },
  {
    path: "/pdf417-generator",
    label: "PDF417 Generator",
    tagline: "Stacked 2D for IDs & passes",
    description:
      "Encode up to ~1850 characters as a PDF417 — the symbology behind driver's licences, IATA boarding passes and shipping manifests. Tunable EC level, columns and row height.",
    icon: Rows3,
    formats: ["PDF417", "EC 0–8", "Compact mode"],
  },
  {
    path: "/aztec-code-generator",
    label: "Aztec Code Generator",
    tagline: "Boarding passes & transit",
    description:
      "Generate Aztec 2D codes — the symbology behind IATA mobile boarding passes and European rail tickets. Adjustable error-correction percentage and layer count.",
    icon: Target,
    formats: ["Aztec", "Compact", "EC 5–95%"],
  },
];

const FAQ = [
  {
    q: "Which Generator Should I Use for a Product I Sell at Retail?",
    a: "If you already have a UPC or EAN allocated from GS1, use the Barcode Generator and pick UPC-A or EAN-13. If you're preparing for GS1 Sunrise 2027, also generate the same GTIN as a GS1 Digital Link so you have a 2D version ready for the dual-barcode transition.",
  },
  {
    q: "Do I Need to Install or Sign up for Anything?",
    a: "No. Every generator runs entirely in your browser using open-source libraries (bwip-js for 1D / Data Matrix, qrcode for QR). Nothing about your codes, Wi-Fi credentials or contact details is sent to a server.",
  },
  {
    q: "Can I Download the Output as SVG for Print?",
    a: "Yes. All generators offer a vector SVG download in addition to a high-resolution PNG. Use SVG when you need to scale to packaging or large signage without quality loss.",
  },
  {
    q: "What's the Difference Between a QR Code and a Data Matrix?",
    a: "Both are 2D barcodes, but Data Matrix codes are usually smaller and are the default for industrial, medical and aerospace marking. QR codes are more common in consumer-facing scenarios because nearly every phone camera decodes them natively. See the 1D vs 2D guide for a full comparison.",
  },
  {
    q: "How Do I Make a QR Code That Joins Someone to My Wi-Fi?",
    a: "Use the Wi-Fi QR Code Generator. Enter your SSID, password and the security type (typically WPA/WPA2). The generator builds the standard WIFI: URI scheme that iOS and Android camera apps recognise automatically.",
  },
  {
    q: "Are the Generated Barcodes Free to Use Commercially?",
    a: "Yes. The generators are free for personal and commercial use. Note that the barcode identifier itself (your GTIN, ISBN, etc.) must be allocated by the relevant authority — ReadBarcode only renders the symbol, it doesn't issue identifiers.",
  },
  {
    q: "Are Barcodes and QR Codes the Same Thing?",
    a: "No. In everyday use a barcode means a linear row of bars and spaces that holds a short number, while a QR code is a square matrix that holds far more data and is read by a camera. Both are machine-readable symbols, which is why QR codes are often called 2D barcodes.",
  },
  {
    q: "Can You Convert a QR Code to a Barcode?",
    a: "Only when the QR code holds data a linear barcode can carry. Decode the QR code with the QR Code Reader, then enter the result in the Barcode Generator: Code 128 takes short text and digits, while UPC and EAN take only numbers of the right length. A long web address or Wi-Fi string makes a 1D barcode too wide to print or scan comfortably.",
  },
  {
    q: "Is an Online Barcode and QR Code Generator Free?",
    a: "Every generator on ReadBarcode is free, with no account and no watermark on the PNG or SVG file. The QR codes are static: the data is stored in the code itself rather than behind a redirect service, so they keep working without a subscription.",
  },
];

export const Route = createFileRoute("/generators")({
  component: GeneratorsPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESC,
          url: absoluteUrl(PATH),
          hasPart: GENERATORS.map((g) => ({
            "@type": "SoftwareApplication",
            name: g.label,
            description: g.description,
            url: absoluteUrl(g.path),
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Any (Web)",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          })),
        }),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function GeneratorsPage() {
  return (
    <ToolLayout
      eyebrow="Hub"
      title="Barcode & QR Code Generators"
      description="Every ReadBarcode generator in one place — pick the format you need and download a print-ready PNG or SVG in a couple of clicks. The ten generators cover retail and shipping barcodes, QR codes for links, Wi-Fi and contacts, and Data Matrix, PDF417 and Aztec codes, all rendered in your browser."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the ReadBarcode Generators"
            about={
              <>
                <p>
                  The generator hub brings together every barcode and QR builder on ReadBarcode.com.
                  Each tool runs entirely in your browser — the symbology is rendered locally
                  with <strong>bwip-js</strong> (1D and Data Matrix) or <strong>qrcode</strong> (QR),
                  so nothing about the contents you encode leaves your device.
                </p>
                <p>
                  Pick the format that matches your channel: a retail UPC or EAN for a shelf product, a
                  QR for a marketing landing page, a Wi-Fi QR for guest networks, a vCard QR for a
                  business card, a Data Matrix for a small industrial part, or a GS1 Digital Link to
                  prepare for the <Link to="/barcode-guides/sunrise-2027-explained">GS1 Sunrise 2027</Link> transition
                  to 2D at point of sale.
                </p>
              </>
            }
            whenToUse={[
              "You're setting up packaging for a new SKU and need a print-ready barcode.",
              "You're sharing your Wi-Fi with guests and don't want them mistyping the password.",
              "You're prepping a conference booth or business card and want a vCard QR.",
              "You're labelling small parts or medical devices that need a compact 2D symbol.",
              "You're piloting GS1 Digital Link on packaging ahead of the 2027 sunrise.",
              "You're embedding a QR in marketing material and need control over colour and error correction.",
            ]}
            howItWorks={[
              {
                title: "Choose a generator",
                desc: "Pick the card above that matches the symbology you need — 1D barcode, QR, Data Matrix or GS1 Digital Link.",
              },
              {
                title: "Enter your payload",
                desc: "Type or paste the GTIN, URL, contact card or Wi-Fi credentials. Each tool validates the input as you go.",
              },
              {
                title: "Tune the output",
                desc: "Adjust size, error correction, colours or AI options depending on the generator.",
              },
              {
                title: "Download PNG or SVG",
                desc: "Save a high-resolution PNG for the web or a vector SVG for print and packaging.",
              },
              {
                title: "Verify with a reader",
                desc: "Round-trip-check by scanning the file in the matching ReadBarcode reader — read the QR, decode the Data Matrix, or run a GTIN validator.",
              },
            ]}
            whatToAvoid={[
              "Don't print at sub-300 DPI for retail — scanners struggle with bar-width inaccuracy at low resolution.",
              "Don't generate a UPC/EAN with a number you haven't been allocated by GS1; the symbol may scan but it isn't a valid product identifier.",
              "Don't use the lowest QR error-correction level (L) on outdoor or laminated surfaces where a smudge can hide several modules.",
              "Don't embed long URLs in a small Data Matrix — the symbol density may exceed what handheld scanners can resolve reliably.",
              "Don't forget to test the printed output on the actual substrate and with the actual scanner you'll deploy.",
            ]}
            tips={[
              "Generate the SVG first and rasterise to PNG only for the final asset — you'll always have a clean vector source if you need to resize.",
              "For QR codes used in print, use error-correction level Q or H so they still scan after light damage or ink bleed.",
              "When generating GS1 Digital Link, render the same payload as both QR and Data Matrix so retail and industrial scanners both work.",
              "Pair each generator with the matching reader to round-trip the output — for example, generate then re-decode with the Data Matrix reader.",
              "Use the Barcode Type Detector if you're unsure which symbology a downstream system expects.",
            ]}
          />
          <h2>Barcode or QR Code: Which Should You Generate?</h2>
          <p>Generate a barcode when the code must work with checkout or warehouse scanners, and a QR code when people will scan it with a phone camera. Many retail products carry both.</p>
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">Question</th>
                  <th scope="col">Linear barcode (UPC, EAN, Code 128)</th>
                  <th scope="col">QR code</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>What does it hold?</td>
                  <td>A short number, such as an EAN-13</td>
                  <td>A web address, text, Wi-Fi details or a contact card</td>
                </tr>
                <tr>
                  <td>What reads it?</td>
                  <td>Laser and camera-based scanners</td>
                  <td>Phone cameras and camera-based scanners</td>
                </tr>
                <tr>
                  <td>Where is it used?</td>
                  <td>Price lookup, stock control, shipping</td>
                  <td>Links, menus, guest Wi-Fi, business cards</td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <p>QR is one 2D option among several: the <Link to="/data-matrix-generator">Data Matrix generator</Link> suits small parts, the <Link to="/pdf417-generator">PDF417 generator</Link> suits ID cards, and the <Link to="/aztec-code-generator">Aztec code generator</Link> suits tickets shown on a phone. Shipping cartons take an <Link to="/itf-14-label-generator">ITF-14 carton label</Link>, and contact details fit a <Link to="/vcard-qr-generator">vCard QR code</Link>.</p>
          <p>Each tool is an online barcode and QR code generator that runs in the browser, so there is no barcode and QR code generator app to install. The <Link to="/barcode-guides/1d-vs-2d-barcodes">guide to 1D vs 2D barcodes</Link> compares both families.</p>
          <h3>Can You Make a QR Code From a Barcode?</h3>
          <p>Yes, but only the number carries over. Decode the barcode with the <Link to="/barcode-reader">online barcode reader</Link> and paste the digits into the <Link to="/qr-code-generator">QR code generator</Link>. The result is plain text, not a replacement for a retail barcode.</p>
          <p>For a retail-ready 2D code, the <Link to="/gs1-digital-link-generator">GS1 Digital Link generator</Link> puts the GTIN in a web address and renders it as a QR code or Data Matrix.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={
        <section className="mt-12">
          <h2 className="text-xl font-semibold tracking-tight md:text-2xl">
            Related Tools and Guides
          </h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              {
                to: "/barcode-reader",
                label: "Online Barcode Reader",
                desc: "Decode any 1D or 2D barcode you've just generated, straight from camera or upload.",
              },
              {
                to: "/barcode-type-detector",
                label: "Barcode Type Detector",
                desc: "Paste a value and identify the likely format — useful when choosing which generator to use.",
              },
              {
                to: "/gtin-validator",
                label: "GTIN Validator",
                desc: "Validate GTIN-8/12/13/14 check digits before generating the barcode.",
              },
              {
                to: "/barcode-guides/1d-vs-2d-barcodes",
                label: "1D vs 2D barcodes",
                desc: "Field guide to when each symbology makes sense on packaging.",
              },
              {
                to: "/data-matrix-generator",
                label: "Data Matrix Generator",
                desc: "Compact 2D codes for small parts, UDI and GS1 payloads.",
              },
              {
                to: "/pdf417-generator",
                label: "PDF417 Generator",
                desc: "Stacked 2D codes for ID cards, passes and manifests.",
              },
              {
                to: "/aztec-code-generator",
                label: "Aztec Code Generator",
                desc: "Square 2D codes for tickets and mobile boarding passes.",
              },
              {
                to: "/itf-14-label-generator",
                label: "ITF-14 Carton Label Generator",
                desc: "Shipping carton barcodes with bearer bars.",
              },
              {
                to: "/vcard-qr-generator",
                label: "vCard QR Code Generator",
                desc: "Contact details a phone saves in one scan.",
              },
            ].map((t) => (
              <Link
                key={t.to}
                to={t.to}
                className="group flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-4 transition hover:border-primary/50 hover:bg-muted/40"
              >
                <div>
                  <div className="text-sm font-semibold">{t.label}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
                </div>
                <ArrowRight
                  className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden
                />
              </Link>
            ))}
          </div>
        </section>
      }
    >
      <section aria-label="Generators">
        <div className="grid gap-4 sm:grid-cols-2">
          {GENERATORS.map((g) => {
            const Icon = g.icon;
            return (
              <Link
                key={g.path}
                to={g.path}
                className="group flex h-full flex-col rounded-lg border border-border bg-background p-5 transition hover:border-primary/50 hover:bg-muted/30"
              >
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  {g.tagline}
                </div>
                <h2 className="mt-3 text-lg font-semibold leading-snug group-hover:text-primary">
                  {g.label}
                </h2>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{g.description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {g.formats.map((f) => (
                    <span
                      key={f}
                      className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Open generator
                  <ArrowRight
                    className="h-3.5 w-3.5 transition group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </span>
              </Link>
            );
          })}
        </div>
      </section>
    </ToolLayout>
  );
}
